import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import {
  useCurrentOperationalPeriod,
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';

import { IAP_DASHBOARD_MODES } from 'components/IAP/ICSConstants';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useAutoZoomToHomeAor } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/useAutoZoomToHomeAor';
import classNames from 'classnames';

export const PeriodButtonGroup = () => {
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const periodNumber = currentPeriod?.period_number ?? 0;
  const { data: incident } = useIncident();
  const isPlanningP = periodNumber > 0;
  const isInitialResponse = periodNumber === 0;
  const isEscalatedNoPeriod = !!incident?.escalated_at && periodNumber === 0;
  const { isMapVisible } = useUscgMapContext();
  const showMapToggle = (isPlanningP || isInitialResponse) && !isMapVisible;

  const { setIsFuture, mode, setMode } = useICSContext();

  const { data: incidentPermissions } = useIncidentPermissions();

  const canViewFuturePeriod =
    incidentPermissions?.['incident:viewFuturePeriod'] ?? false;

  const isCurrent = mode !== IAP_DASHBOARD_MODES.FUTURE || !canViewFuturePeriod;
  const curPeriodNum = periodNumber;
  const futurePeriodNum = curPeriodNum > 0 ? curPeriodNum + 1 : 1;
  const currentPeriodLabel: number | 'None' =
    periodNumber > 0 ? periodNumber : 'None';

  if (!isPlanningP && !isInitialResponse) {
    return null;
  }

  if (curPeriodNum === 0 && !isEscalatedNoPeriod) {
    return (
      <div className="d-flex align-items-center justify-content-end">
        {showMapToggle && (
          <div className="d-none d-md-flex">
            <ToggleMapButton />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-end">
      {/* Full period buttons — xl and above */}
      <div className="iap-button-group d-none d-xl-flex">
        <div
          className={classNames({
            'iap-buttons': canViewFuturePeriod,
          })}
        >
          <StylishNewButton
            onClick={() => {
              setMode(IAP_DASHBOARD_MODES.CURRENT);
              setIsFuture(false);
            }}
            className="button button--secondary button-one"
            style={
              isCurrent
                ? {
                    backgroundColor: 'var(--success-600)',
                    color: 'var(--text-white)',
                  }
                : {}
            }
          >
            Current Period: {currentPeriodLabel}
          </StylishNewButton>
          {canViewFuturePeriod && (
            <StylishNewButton
              onClick={() => {
                setMode(IAP_DASHBOARD_MODES.FUTURE);
                setIsFuture(true);
              }}
              className="button button--secondary button-two"
              style={
                !isCurrent
                  ? {
                      backgroundColor: 'var(--brand-primary)',
                      color: 'var(--text-white)',
                    }
                  : {}
              }
            >
              Future Period: {futurePeriodNum}
            </StylishNewButton>
          )}
        </div>
      </div>

      {/* Compact period buttons — below xl */}
      <div className="period-buttons-compact d-flex d-xl-none">
        <StylishNewButton
          onClick={() => {
            setMode(IAP_DASHBOARD_MODES.CURRENT);
            setIsFuture(false);
          }}
          className="button button--secondary"
          style={
            isCurrent
              ? {
                  backgroundColor: 'var(--success-600)',
                  color: 'var(--text-white)',
                }
              : {}
          }
        >
          {currentPeriodLabel}
        </StylishNewButton>
        {canViewFuturePeriod && (
          <StylishNewButton
            onClick={() => {
              setMode(IAP_DASHBOARD_MODES.FUTURE);
              setIsFuture(true);
            }}
            className="button button--secondary"
            style={
              !isCurrent
                ? {
                    backgroundColor: 'var(--brand-primary)',
                    color: 'var(--text-white)',
                  }
                : {}
            }
          >
            {futurePeriodNum}
          </StylishNewButton>
        )}
      </div>

      {showMapToggle && (
        <div className="d-none d-md-flex">
          <ToggleMapButton />
        </div>
      )}
    </div>
  );
};

export function ToggleMapButton() {
  const { isMapVisible, setIsMapVisible } = useUscgMapContext();
  const { zoomToHomeAor } = useAutoZoomToHomeAor();

  const handleToggle = async () => {
    const nextVisible = !isMapVisible;
    setIsMapVisible(nextVisible);

    if (nextVisible) {
      // Wait for DOM to update after setIsMapVisible
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
      try {
        await zoomToHomeAor();
      } catch (error) {
        console.error('Failed to zoom to home AOR:', error);
      }
    }
  };

  return (
    <StylishNewButton
      className={classNames('ms-1 me-1', {
        'button--primary gap-1': isMapVisible,
        'button--secondary gap-1': !isMapVisible,
      })}
      onClick={handleToggle}
    >
      <SharedIcon iconName="map" />
    </StylishNewButton>
  );
}
